import { useEffect, useState } from 'react';
import { HeartPulse, ShieldCheck, PhoneCall, CheckCircle2 } from 'lucide-react';
import { get, post } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Field, Input, Loader, SectionTitle, Badge } from '../../components/UI';

const BLANK = {
  chronic: { has: false, details: '', medication: '', allergies: '' },
  emergency: { name: '', relation: '', mobile: '', altMobile: '' },
  liabilityAccepted: false,
};

/**
 * Health and emergency details, asked once per event. `onDone` is passed by
 * the gate that blocks the rest of the portal until this is answered.
 */
export default function AttendeeDeclaration({ onDone }) {
  const { eventId } = useEvents();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    get('/declarations/me', eventId ? { event: eventId } : {})
      .then((d) => {
        if (!alive) return;
        setData(d);
        if (d.declaration) {
          setF({
            chronic: { ...BLANK.chronic, ...(d.declaration.chronic || {}) },
            emergency: { ...BLANK.emergency, ...(d.declaration.emergency || {}) },
            liabilityAccepted: Boolean(d.declaration.liabilityAccepted),
          });
        }
      })
      .catch((err) => toast.error(err.message));
    return () => { alive = false; };
  }, [eventId, toast]);

  if (!data) return <Loader full label="Loading" />;
  const s = data.settings || {};
  if (!s.enabled) {
    return (
      <>
        <SectionTitle title="Health details" />
        <Card padding={24}><p style={{ fontSize: 13.5, color: 'var(--text-mid)' }}>Nothing to fill in for this event.</p></Card>
      </>
    );
  }

  const set = (group, patch) => setF({ ...f, [group]: { ...f[group], ...patch } });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await post('/declarations', { ...f, event: data.event?._id || eventId, from: 'web' });
      toast.success(res.message);
      setData({ ...data, declaration: res.data, pending: false });
      onDone?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SectionTitle
        title={s.title || 'Before you travel'}
        subtitle={data.event?.title}
        action={data.declaration ? <Badge tone="success"><CheckCircle2 size={11} /> Answered</Badge> : null}
      />
      <form onSubmit={submit} style={{ maxWidth: 620, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {s.note && <p style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.7 }}>{s.note}</p>}

        {s.askChronic && (
          <Card padding={22}>
            <p style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HeartPulse size={16} color="var(--gold-500)" /> Any ongoing medical condition?
            </p>
            <p style={{ fontSize: 12.5, color: 'var(--text-lo)', marginTop: 4, lineHeight: 1.6 }}>
              Only the organising team sees this. It helps us look after you if something happens on the trip.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              {[[false, 'No'], [true, 'Yes']].map(([v, label]) => (
                <button key={label} type="button" onClick={() => set('chronic', { has: v })}
                  style={{
                    flex: 1, padding: '11px 14px', fontSize: 13.5, borderRadius: 'var(--r)',
                    border: `1px solid ${f.chronic.has === v ? 'var(--gold-500)' : 'var(--border)'}`,
                    background: f.chronic.has === v ? 'rgba(239,212,138,0.1)' : 'transparent',
                    color: f.chronic.has === v ? 'var(--gold-300)' : 'var(--text-mid)',
                  }}>
                  {label}
                </button>
              ))}
            </div>
            {f.chronic.has && (
              <div style={{ marginTop: 14 }}>
                <Field label="Which condition?" required>
                  <Input value={f.chronic.details} onChange={(e) => set('chronic', { details: e.target.value })}
                    placeholder="Diabetes, asthma, blood pressure…" />
                </Field>
                <Field label="Medication you carry" hint="Optional">
                  <Input value={f.chronic.medication} onChange={(e) => set('chronic', { medication: e.target.value })} placeholder="Inhaler, insulin…" />
                </Field>
              </div>
            )}
            <Field label="Allergies" hint="Food, medicine or anything else. Leave blank if none.">
              <Input value={f.chronic.allergies} onChange={(e) => set('chronic', { allergies: e.target.value })} placeholder="Peanuts, penicillin…" />
            </Field>
          </Card>
        )}

        {s.askEmergency && (
          <Card padding={22}>
            <p style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <PhoneCall size={16} color="var(--gold-500)" /> Who should we call in an emergency?
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
              <Field label="Name" required><Input value={f.emergency.name} onChange={(e) => set('emergency', { name: e.target.value })} /></Field>
              <Field label="Relationship"><Input value={f.emergency.relation} onChange={(e) => set('emergency', { relation: e.target.value })} placeholder="Spouse, parent, friend…" /></Field>
              <Field label="Phone number" required><Input value={f.emergency.mobile} onChange={(e) => set('emergency', { mobile: e.target.value })} inputMode="tel" placeholder="98765 43210" /></Field>
              <Field label="Another number" hint="Optional"><Input value={f.emergency.altMobile} onChange={(e) => set('emergency', { altMobile: e.target.value })} inputMode="tel" /></Field>
            </div>
          </Card>
        )}

        {s.requireLiability && (
          <Card padding={22}>
            <label style={{ display: 'flex', gap: 11, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={f.liabilityAccepted} onChange={(e) => setF({ ...f, liabilityAccepted: e.target.checked })}
                style={{ accentColor: 'var(--gold-400)', width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.7 }}>
                <ShieldCheck size={14} color="var(--gold-500)" style={{ verticalAlign: '-2px', marginRight: 6 }} />
                {s.liabilityText}
              </span>
            </label>
          </Card>
        )}

        <Button type="submit" size="lg" loading={saving} style={{ width: '100%' }}>
          {data.declaration ? 'Update my details' : 'Submit and continue'}
        </Button>
      </form>
    </>
  );
}
